import { useMyMonitoringGetSiteZones } from '@apps-common/apis/my-monitoring/zones/get-site-zones'
import { ZoneListCard, ZoneListCardSkeleton } from '@apps-common/apps/portal/pages/units/pages/manage/pages/zones/components'
import { ZoneModifyModal } from '@apps-common/apps/portal/pages/units/pages/manage/pages/zones/modals/zone-modify-modal'
import { NoResultsAddCard, PullRefresh } from '@apps-common/components/common'
import { PageSubtitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundLg, ButtonRoundSm } from '@apps-common/components/elements'
import { ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { useMyMonitoringSiteContextStore } from '@apps-common/stores'
import { IMMSiteModel } from '@apps-common/types/my-monitoring'
import { useIonModal } from '@ionic/react'
import { motion } from 'framer-motion'

interface IProps {
  loading: boolean
  site?: IMMSiteModel
  back: () => void
}

export const SiteZoneView: React.FC<IProps> = ({ loading, site, back }) => {
  const { isAdmin } = useMyMonitoringSiteContextStore()

  const { data, isLoading, isError, refetch } = useMyMonitoringGetSiteZones({
    config: { enabled: !!site?.unitId },
    unitId: site?.unitId || ''
  })

  const [present, dismiss] = useIonModal(ZoneModifyModal, {
    componentProps: {
      unitId: site?.unitId
    },
    onDismiss: (data: string, role: string) => dismiss(data, role),
    enableBackdropDismiss: true
  })

  loading = loading || !site || !data || isLoading || isError

  return (
    <>
      <PullRefresh loading={loading} callback={refetch} />
      <div className=' flex flex-col gap-4 max-w-[430px] lg:max-w-full mx-auto'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center justify-start gap-4'>
            <ButtonRoundSm
              type='button'
              className='bg-ctx-mm-brand text-ctx-on-mm-brand'
              callback={() => {
                if (back) {
                  back()
                }
              }}
            >
              <commonIcons.arrowRight className='rotate-180' />
            </ButtonRoundSm>
            <PageSubtitle classes='text-text'>Zone Descriptions</PageSubtitle>
          </div>
          {isAdmin && (
            <ModalButton
              component={ZoneModifyModal}
              disabled={loading}
              componentProps={{
                unitId: site?.unitId
              }}
              enterAnimation={modalSlideUpEnterAnimation}
              leaveAnimation={modalSlideUpLeaveAnimation}
              classes='relative w-[40px] h-[40px] rounded-[20px] flex items-center justify-center rounded-3xl'
            >
              <ButtonRoundLg
                type='div'
                className='action-success'
                tooltip={{ id: 'device-zones-add-new', place: 'top', message: 'Add new zone' }}
              >
                <commonIcons.add className='!text-[1.1rem]' />
              </ButtonRoundLg>
            </ModalButton>
          )}
        </div>
        <div className='grid lg:grid-cols-2 gap-4'>
          {loading && Array.from(Array(4).keys()).map((k) => <ZoneListCardSkeleton key={`site-zone-list-card-skeleton-${k}`} />)}
          {!loading &&
            data?.map((z, i) => (
              <motion.div key={`site-zone-card-${z.unitCustomZoneDescriptionId}-${i}`} {...motionFadeIn(0.25)}>
                <ZoneListCard unitZone={z} disabled={!isAdmin} />
              </motion.div>
            ))}
          {!loading && data?.length === 0 && (
            <NoResultsAddCard
              title='Nothing to see here.'
              message='This site has no zone descriptions'
              callback={() =>
                present({
                  enterAnimation: modalSlideUpEnterAnimation,
                  leaveAnimation: modalSlideUpLeaveAnimation
                })
              }
              disabled={loading || !isAdmin}
            />
          )}
        </div>
      </div>
    </>
  )
}
